// Source of truth for Mattermost data.
// If more models from @dialogue/messages are needed by app code, export them from the library here
// rather than importing from the library directly.
// If more models from @mattermost/types are needed by app code, export them from this module
// rather than importing from the library directly.

// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import type {
  Post as MattermostPost,
  PostList as MattermostPostList,
} from '@mattermost/types/posts'
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import type {
  ChoiceOption,
  Fullscreen,
  ListMetadata,
  Message,
  MessagePropsUnion,
  PromptChoiceImageMapPropsComplete,
  Polygon,
  AppActionEnum as _AppActionEnum,
  OutlinedCardTypeEnum as _OutlinedCardTypeEnum,
  RichContentTypeEnum as _RichContentTypeEnum,
  PromptTypeEnum,
} from '@dialogue/messages'
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
export type {
  ActionItem,
  Back,
  RichContentUnion,
  ScaleLevel,
  PromptCheckoutProps,
  Text as RichContentText,
  MessagePropsUnion,
} from '@dialogue/messages'
export type { ChoiceOption, ListMetadata, PromptChoiceImageMapPropsComplete }
export type Coordinates = Polygon[]
export type {
  ChannelMembership as MattermostChannelMembership,
  ServerChannel as MattermostChannel,
} from '@mattermost/types/channels'

// ============================================================================
// ENUM-LIKE COMPATIBILITY
// @dialogue/messages exposes string unions not Typescript enums, unfortunately.
// We create enum-like objects here for simplicity of use.
// ============================================================================

export const AppActionEnum = {
  Archive: 'ARCHIVE',
  Undo: 'UNDO',
} as const satisfies Record<string, _AppActionEnum>

export const OutlinedTypeEnum = {
  Default: 'default',
  Info: 'info',
  Action: 'action',
  Negative: 'negative',
  Highlight: 'highlight',
} as const satisfies Record<string, _OutlinedCardTypeEnum>

export type OutlinedTypeEnumValues = _OutlinedCardTypeEnum

export const RichContentTypeEnum = {
  IconCard: 'icon_card',
  Outlined: 'outlined_card',
  Collapsible: 'collapsible_card',
  Scale: 'scale_card',
  Text: 'text',
  Steps: 'steps',
  Appointment: 'appointment',
  GroupedSection: 'grouped_section',
} as const satisfies Record<string, _RichContentTypeEnum>

export const IntakeMessageTypeEnum = {
  prompt_text: 'prompt_text',
  prompt_choice_single: 'prompt_choice_single',
  prompt_choice_multi: 'prompt_choice_multi',
  prompt_choice_search: 'prompt_choice_search',
  prompt_choice_image_map: 'prompt_choice_image_map',
  prompt_booking: 'prompt_booking',
  prompt_checkout: 'prompt_checkout',
  prompt_address: 'prompt_address',
  prompt_file: 'prompt_file',
  prompt_provider_list: 'prompt_provider_list',
  prompt_multi_subprompt: 'prompt_multi_subprompt',
} as const satisfies Partial<Record<PromptTypeEnum, PromptTypeEnum>>

// ============================================================================
// AUGMENTED MATTERMOST TYPES
// @mattermost/types doesn't include our custom props model, so we augment them
// and export the final models only.
// ============================================================================

export type RawPost = Omit<MattermostPost, 'type' | 'props'> &
  Omit<Message, 'props'> & {
    // dialogue.messages uses inheritance to define concrete props models,
    // and Message['props'] only points to the base model for props unfortunately
    props: MessagePropsUnion
  }

export type RawPostList = Omit<MattermostPostList, 'posts'> & {
  posts: Record<string, RawPost>
}

export type NewPost = Pick<RawPost, 'channel_id' | 'message'> & {
  props: Partial<RawPost['props']>
} & Partial<Omit<RawPost, 'channel_id' | 'message' | 'props'>>

// ============================================================================
// HISTORICAL COMPATIBILITY
// These custom derived models are only used to support the implementation of
// historical compatibility in the Member App, after specific props have been
// deprecated.
// ============================================================================
// TODO: This should be replaced with direct imports from dialogue.messages
export interface IntakeAnswer {
  display?: string
  props?: Record<string, unknown>
  payload?: unknown
  fileObj?: unknown
}

// TODO(DIA-88912): remove these models and instead simply export Fullscreen
// from @dialogue/messages
export enum DescriptionStyle {
  card = 'card',
}
export interface MemberAppFullscreen extends Fullscreen {
  description_style?: DescriptionStyle | null
}
